import { ChallengeEditing } from '@/components/admin/lessons/ChallengeEditing';
import ListActivities from '@/components/admin/lessons/ListActivities';
import MaterialContainer from '@/components/admin/lessons/material/MaterialContainer';
import {
  Pane,
  serializerTabsContents,
} from '@/components/ui/custom/dataDisplay/Tabs/Tabs';
import LessonResourceContainer from '@/components/_deprecated/resource/lesson/LessonResourceContainer';
import { useListCache } from '@/data/hook/query/v2/useCacheManager';
import { booksQueryKeys } from '@/data/services/querykeys';
import CoursePath from '@/models/CoursePath';
import Lesson from '@/models/Lesson';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import useLessons from '@/pages/admin/ManageCourseEditing.hook';
import { getSelectedLesson } from '@/utils/lesson/box';
import { ROLES } from '@/utils/roles';
import {
  BookOpenIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  InformationCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useLessonsBlockWithLessons } from '@/pages/admin/courses/studio/CourseStudio.hooks';
import LessonDetailForm from './LessonDetailForm';

export default function useLessonBox() {
  const { slug, lessonOrder } = useParams();

  const {
    lessons: lessonsInBlock,
    lessonBlocksWithLessons,
    isLoading: isLoadingLessons,
    invalidateBlock,
  } = useLessonsBlockWithLessons(slug);

  const { directLessons: lessonsOnly, updateLessons } = useLessons(slug ?? '');

  const lessons =
    lessonBlocksWithLessons.length > 0 ? lessonsInBlock : lessonsOnly;
  const lesson = getSelectedLesson(lessons, lessonOrder);

  const block = lessonBlocksWithLessons.find(block =>
    block.lessons.some(current => current.id === lesson?.id),
  );

  const lessonsQueryKey = useMemo(() => {
    return lessonBlocksWithLessons.find(block =>
      block.lessons.some(lesson => lesson.order === Number(lessonOrder)),
    )?.lessonsQueryKey;
  }, [lessonBlocksWithLessons, lessonOrder]);

  const lessonsInBlockCacheManager = useListCache<Lesson>(
    lessonsQueryKey as QueryKey,
  );

  const isLoading = isLoadingLessons;
  return {
    lessonBlocksWithLessons,
    lesson,
    lessons,
    block,
    lessonsQueryKey,
    lessonsInBlockCacheManager,
    isLoading,
    invalidateBlock,
    updateLessons,
  };
}

export const useLessonTabs = (
  lesson: Lesson,
  coursePath?: CoursePath,
  block?: TempLessonBlock | null,
  customUpdateLessons?: (lessonId?: number) => Promise<void>,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { invalidateBlock } = useLessonsBlockWithLessons(coursePath?.slug);
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get('tab') || 'details',
  );
  const { t } = useTranslation('translation', {
    keyPrefix: 'common.lessonTabs',
  });

  const updateLessons = async (lessonId?: number) => {
    await customUpdateLessons?.(lessonId);
    if (block?.id) await invalidateBlock(block.id);
  };

  async function updateMaterial(bookId: number) {
    await queryClient.invalidateQueries({
      queryKey: booksQueryKeys.get(bookId).queryKey,
    });
  }

  const teacherBookId = lesson.teacherBook;
  const studentBookId = lesson.book?.id;

  const isStudentBook = activeTab === 's-material';

  const handleTabChange = async (tab: string) => {
    setActiveTab(tab);
    setSearchParams({ tab });

    const bookId = isStudentBook ? studentBookId : teacherBookId;

    if (bookId && ROLES.isValidId(bookId)) {
      await updateMaterial(bookId);
    }
  };

  const panes: Pane[] = [
    {
      key: 'details',
      text: t('details'),
      icon: InformationCircleIcon,
      content: <LessonDetailForm />,
    },
    {
      key: 's-material',
      text: t('material'),
      icon: BookOpenIcon,
      content: (
        <MaterialContainer
          key={studentBookId}
          updateLessons={updateLessons}
          bookType="STUDENT"
          course={coursePath}
          lesson={lesson}
          blockId={block?.id}
        />
      ),
    },
    {
      key: 't-material',
      text: t('teacherMaterial'),
      icon: BookOpenIcon,
      content: (
        <MaterialContainer
          key={teacherBookId}
          updateLessons={updateLessons}
          bookType="TEACHER"
          course={coursePath}
          lesson={lesson}
          blockId={block?.id}
        />
      ),
    },
    {
      key: 'resource',
      text: t('resource'),
      icon: FolderIcon,
      content: (
        <LessonResourceContainer
          updateLessons={updateLessons}
          coursePath={coursePath}
          lesson={lesson}
          blockId={block?.id}
        />
      ),
    },
    {
      key: 'activities',
      text: t('activity'),
      icon: ClipboardDocumentListIcon,
      content: (
        <ListActivities
          key={lesson.id}
          updateLessons={updateLessons}
          lesson={lesson}
          coursePath={coursePath}
          blockId={block?.id}
        />
      ),
    },
    {
      key: 'challenge',
      text: t('challenge'),
      icon: BoltIcon,
      content: (
        <ChallengeEditing
          onUpdate={updateLessons}
          lesson={lesson}
          blockId={block?.id}
          slug={coursePath?.slug}
        />
      ),
    },
  ];

  const tabs = serializerTabsContents(panes);

  return {
    activeTab,
    setActiveTab: handleTabChange,
    tabs,
    panes,
  };
};
